import { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  useGcodeFile,
  useRunState,
  useMotionLine,
  useProgramLineCount,
  useSendCommand,
  useMachineStore,
} from '@/store/store';
import type { GCodeFile } from '@/types/machine.types';
import {
  GCodePreviewModal,
  type ProgramMetadata,
} from '@/components/GCodePreviewModal/GCodePreviewModal';
import { AIAssistantModal } from '@/components/AIAssistantModal/AIAssistantModal';
import { CloseIcon, PreviewIcon, SparkleIcon, SparkleSmallIcon, UploadIcon } from '@/icons';

// three.js is heavy — only load the simulator (and three) when it's opened.
const GCodeSimulator = lazy(() =>
  import('@/components/GCodeSimulator/GCodeSimulator').then((m) => ({ default: m.GCodeSimulator })),
);
import styles from '@/components/GCodePanel/GCodePanel.module.css';
import { truncateFilename } from '@/lib/truncateFilename';
import { buildToolpath } from '@/lib/gcodeToolpath';

// TODO(estimated-time): feed-per-rev (G99) lathe programs can't be timed reliably by the
// pure parser (needs live spindle RPM under G96). Left as a placeholder until decided.
const PLACEHOLDER_ESTIMATED_TIME = '00:24:37';

export function GCodePanel(): JSX.Element {
  const [showPreview, setShowPreview] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const gcodeFile = useGcodeFile();
  const runState = useRunState();
  const motionLine = useMotionLine();
  const programLineCount = useProgramLineCount();
  const sendCommand = useSendCommand();

  // Single-block stepping: 0-based index of the last line sent via MDI (-1 = not started).
  const [singleBlockLine, setSingleBlockLine] = useState(-1);

  const setGcodeFile = useMachineStore((s) => s.setGcodeFile);
  const setRunState = useMachineStore((s) => s.setRunState);

  // File upload handler
  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        // Uploaded files are already valid LinuxCNC G-code (incl. O-word
        // subroutines and #<...> params), so they're sent through untouched.
        const lines = text.split('\n').filter((l) => l.trim() !== '');

        const gcFile: GCodeFile = {
          name: file.name,
          path: window.electronAPI?.getPathForFile?.(file) ?? file.name,
          originalLines: lines,
          modifiedLines: lines,
        };
        setGcodeFile(gcFile);
        setSingleBlockLine(-1);
      };
      reader.readAsText(file);

      e.target.value = '';
    },
    [setGcodeFile],
  );

  // Send full program to backend for AUTO mode execution
  const handleRun = useCallback(() => {
    if (!gcodeFile) return;

    const fullProgram = gcodeFile.modifiedLines.join('\n');
    sendCommand({
      type: 'RUN_PROGRAM',
      gcode: fullProgram,
      filename: gcodeFile.name.replace('.nc', '_modified.nc'),
    });
    setSingleBlockLine(-1);
    setRunState('running');
  }, [gcodeFile, sendCommand, setRunState]);

  // Single Block — send the next program line via MDI and advance the highlight one line.
  // TODO(single-block-busy): the button should show a spinner + be disabled while the
  // machine is executing the current line, re-enabling only when it goes idle. This needs a
  // machine "busy"/interp-idle signal that the STATUS payload does NOT expose yet (only
  // programRunning/programPaused for AUTO runs). Once the backend adds e.g. `busy`, gate
  // here (early-return while busy) and pass `busy` down to disable the Single Block button.
  const handleSingleBlock = useCallback(() => {
    if (!gcodeFile) return;
    const next = singleBlockLine + 1;
    const line = gcodeFile.modifiedLines[next];
    if (line === undefined) return; // program finished
    sendCommand({ type: 'MDI', command: line });
    setSingleBlockLine(next);
  }, [gcodeFile, singleBlockLine, sendCommand]);

  // Feedhold — axes decelerate, spindle keeps running
  const handlePause = useCallback(() => {
    sendCommand({ type: 'PAUSE_PROGRAM' });
  }, [sendCommand]);

  // Cycle Start — resume from feedhold position
  const handleResume = useCallback(() => {
    sendCommand({ type: 'RESUME_PROGRAM' });
  }, [sendCommand]);

  // Abort — cancel program entirely
  const handleStop = useCallback(() => {
    sendCommand({ type: 'STOP_PROGRAM' });
    setSingleBlockLine(-1);
    setRunState('idle');
  }, [sendCommand, setRunState]);

  // Dimension (bounding-box extents) is computed from the parsed toolpath — LinuxCNC's
  // status doesn't carry it. buildToolpath walks the whole program (expanding arcs),
  // which is heavy for large files; gate it on `showPreview` so the parse runs when
  // the modal opens rather than freezing the UI the moment a file is uploaded.
  const metadata = useMemo<ProgramMetadata>(() => {
    if (!gcodeFile || !showPreview) {
      return {
        dimension: { x: 0, y: 0, z: 0 },
        estimatedTime: PLACEHOLDER_ESTIMATED_TIME,
      };
    }
    const tp = buildToolpath(gcodeFile.modifiedLines);
    return {
      dimension: {
        x: tp.max[0] - tp.min[0],
        y: tp.max[1] - tp.min[1],
        z: tp.max[2] - tp.min[2],
      },
      estimatedTime: PLACEHOLDER_ESTIMATED_TIME,
    };
  }, [gcodeFile, showPreview]);

  // Highlighted line: single-block index while stepping, otherwise the backend motion line.
  const activeLine = singleBlockLine >= 0 ? singleBlockLine : motionLine;

  const totalLines =
    programLineCount > 0 ? programLineCount : (gcodeFile?.modifiedLines.length ?? 0);
  const progressPercent =
    totalLines > 0 && motionLine >= 0
      ? Math.min(Math.round(((motionLine + 1) / totalLines) * 100), 100)
      : 0;

  return (
    <div className={styles.gcodePanel} role="region" aria-label="G-Code File Loader">
      {/* Upload bar */}
      <div className={styles.gcodePanelUpload}>
        <label className={styles.gcodePanelUploadField}>
          {gcodeFile ? (
            <span className={styles.gcodePanelFilename} title={gcodeFile.name}>
              {truncateFilename(gcodeFile.name)}
            </span>
          ) : (
            <span className={styles.gcodePanelUploadPlaceholder}>Select a CAM file to process</span>
          )}
          <input type="file" accept=".nc,.gcode,.ngc,.tap" onChange={handleFileUpload} hidden />
        </label>
        {gcodeFile ? (
          <>
            {/* Simulate button disabled (opens three.js GCodeSimulator)
            <button
              className={styles.gcodePanelUploadBtn}
              onClick={() => setShowSimulator(true)}
              type="button"
            >
              <SimulateIcon width={18} height={18} />
              Simulate
            </button>
            */}
            <button
              className={styles.gcodePanelUploadBtn}
              onClick={() => setShowPreview(true)}
              type="button"
            >
              <PreviewIcon width={18} height={18} />
              Preview
            </button>
            <button
              className={`${styles.gcodePanelUploadBtn} ${styles.gcodePanelCloseBtn}`}
              onClick={() => {
                setGcodeFile(null);
                setSingleBlockLine(-1);
              }}
              type="button"
            >
              <CloseIcon width={18} height={18} />
              Close
            </button>
          </>
        ) : (
          <>
            <label className={styles.gcodePanelUploadBtn}>
              <UploadIcon width={18} height={18} />
              Upload
              <input type="file" accept=".nc,.gcode,.ngc,.tap" onChange={handleFileUpload} hidden />
            </label>
            <button
              className={styles.gcodePanelAiBtn}
              onClick={() => setShowAI(true)}
              type="button"
              aria-label="AI Assistant"
            >
              <SparkleIcon width={34.32} height={34.32} />
              <SparkleSmallIcon
                width={13.2}
                height={13.2}
                className={styles.gcodePanelAiBtnSpark}
              />
            </button>
          </>
        )}
      </div>

      {showPreview && gcodeFile && (
        <GCodePreviewModal
          gcodeFile={gcodeFile}
          metadata={metadata}
          runState={runState}
          progressPercent={progressPercent}
          motionLine={activeLine}
          onBack={() => setShowPreview(false)}
          onSingleBlock={handleSingleBlock}
          onRun={handleRun}
          onPause={handlePause}
          onResume={handleResume}
          onStop={handleStop}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showSimulator && gcodeFile && (
        <Suspense fallback={null}>
          <GCodeSimulator
            key={gcodeFile.path}
            gcodeFile={gcodeFile}
            onClose={() => setShowSimulator(false)}
          />
        </Suspense>
      )}

      {showAI && <AIAssistantModal onClose={() => setShowAI(false)} />}
    </div>
  );
}
